import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, BadgeCheck, Cloud, Crown, HardDrive, LifeBuoy, Lock, Mail, Music4, ScrollText, ShieldCheck, Store } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import { DEMO_STEPS, SUPPORT_EMAIL } from "@/lib/creatorConstants";

const TOC = [
  { id: "how-it-works", label: "How it works", icon: Music4 },
  { id: "ownership", label: "Your music & your rights", icon: BadgeCheck },
  { id: "commercial-use", label: "Commercial use", icon: Store },
  { id: "pricing", label: "Pricing & passes", icon: Crown },
  { id: "privacy", label: "Privacy & storage", icon: ShieldCheck },
  { id: "support", label: "Support", icon: LifeBuoy },
];

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} data-testid={`about-${id}`} className="scroll-mt-28 rounded-3xl border border-white/[0.1] bg-[#0a0d1c]/75 p-6 sm:p-8">
      <h2 className="flex items-center gap-3 font-display text-2xl font-bold">
        <span className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white"><Icon className="h-5 w-5" /></span>
        {title}
      </h2>
      <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-white/85">{children}</div>
    </section>
  );
}

function Fact({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-300" />
      <div>
        <p className="font-semibold text-white">{title}</p>
        <p className="mt-1 text-[14px] leading-relaxed text-white/80">{children}</p>
      </div>
    </div>
  );
}

/** /about — ownership, commercial use, pricing, privacy and support in plain language. */
export default function InfoPage() {
  const { hash } = useLocation();

  useEffect(() => {
    const id = hash.replace("#", "");
    if (!id) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <CreatorShell testId="about-page" back="/" title="Help & Information" subtitle="Everything you should know before, during and after making your song — in plain language." wide>
      <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
        <nav aria-label="On this page" className="lg:sticky lg:top-24 lg:self-start">
          <ul className="flex flex-wrap gap-2 lg:flex-col">
            {TOC.map((t) => {
              const Icon = t.icon;
              return (
                <li key={t.id}>
                  <a href={`#${t.id}`} data-testid={`about-toc-${t.id}`} className="flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-4 py-2 font-display text-[14px] font-medium text-white/85 transition-colors hover:bg-white/[0.1] hover:text-white">
                    <Icon className="h-4 w-4 text-blue-300" /> {t.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="space-y-6">
          <Section id="how-it-works" icon={Music4} title="How it works">
            <ol className="grid gap-3 sm:grid-cols-2">
              {DEMO_STEPS.map((s) => (
                <li key={s.key} className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
                  <p className="font-display text-[15px] font-semibold text-white">{s.title}</p>
                  <p className="mt-1 text-[14px] text-white/80">{s.body}</p>
                </li>
              ))}
            </ol>
            <p>
              <strong className="text-white">Simple</strong> mode guides you one step at a time. <strong className="text-white">Advanced</strong> mode opens the full 64-channel studio with every tool, grouped into Create · Record · Edit · Finish. You can switch at any time — your song comes with you.
            </p>
            <Link to="/create/song" data-testid="about-start-link" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-5 py-2.5 font-display text-[14px] font-bold text-white">Create a song <ArrowRight className="h-4 w-4" /></Link>
          </Section>

          <Section id="ownership" icon={BadgeCheck} title="Your music & your rights">
            <p><strong className="text-white">You own what you make.</strong> ELPM Studio claims no ownership of your lyrics, your recordings, your mixes or your masters. We never sell, license or publish your music without your action.</p>
            <ul className="list-disc space-y-2 pl-5">
              <li><strong className="text-white">Your lyrics, your voice, your uploads</strong> — fully yours, always.</li>
              <li><strong className="text-white">AI-generated music</strong> — created on a licensed music-generation service through ELPM's paid account. The rights to use that output are passed to you under that service's terms, which allow commercial use for output generated on a paid plan.</li>
              <li><strong className="text-white">Sharing</strong> — a song is only public if you create a share link or publish a stem pack to the marketplace. You can delete shared items at any time from My Projects.</li>
            </ul>
            <p className="text-[14px] text-white/70">Note: rights in AI-generated music are still an evolving area of law in some countries. If a release is important to you (for example a commercial album), consider having a music lawyer review it.</p>
          </Section>

          <Section id="commercial-use" icon={Store} title="Commercial use">
            <p>You may use songs you make in ELPM Studio for <strong className="text-white">church services, live performance, YouTube, streaming platforms, albums, advertising and client work</strong> — on both Free and Pro.</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Credit is appreciated but not required.</li>
              <li>Please don't use a cloned voice without the consent of the person whose voice it is — voice cloning in ELPM is consent-based.</li>
              <li>Don't upload music you don't have rights to for stem separation or covers.</li>
              <li>Streaming distributors may ask whether a track is AI-assisted — answer honestly; many accept it.</li>
            </ul>
          </Section>

          <Section id="pricing" icon={Crown} title="Pricing & passes">
            <p>Everything you need to make a song — create, record, separate stems, master and download — is <strong className="text-white">free</strong>. <strong className="text-white">Pro</strong> is a paid membership for people who make a lot of music.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Fact icon={Crown} title="Two passes, in US dollars">30-Day Pass $9.99 USD · 1-Year Pass $99.90 USD. Both are one-time payments through Stripe.</Fact>
              <Fact icon={Lock} title="No automatic renewal">A pass simply ends on its last day. Nothing is charged again unless you choose to buy another pass. Buying while a pass is active adds the days on.</Fact>
              <Fact icon={BadgeCheck} title="What Pro adds">Higher daily limits on AI songs, stem separations and voice, the Pro plugin library and premium voice models, and priority in the generation queue.</Fact>
              <Fact icon={HardDrive} title="When your pass ends">Your projects stay saved, playable and downloadable. Only the Pro tools lock until you renew — nothing is deleted.</Fact>
            </div>
            <p className="text-[14px] text-white/70">Open <button type="button" data-testid="about-open-pricing" onClick={() => window.dispatchEvent(new Event("elpm:open-pricing"))} className="font-semibold text-blue-200 underline-offset-2 hover:underline">Pricing</button> from any page to see your current plan.</p>
          </Section>

          <Section id="privacy" icon={ShieldCheck} title="Privacy & storage">
            <div className="grid gap-3 sm:grid-cols-2">
              <Fact icon={HardDrive} title="Browser storage (no sign-in)">Your songs are linked to this browser on this device. If you clear browser data, use private mode or switch devices, they won't appear. Nothing identifies you personally.</Fact>
              <Fact icon={Cloud} title="Saved to your account (sign in, free)">Sign in with Google and your songs, recordings and stems are saved to your account, so you can open them on your phone, laptop or church computer. Anything you made before signing in moves over automatically.</Fact>
            </div>
            <p><strong className="text-white">What we store:</strong> the songs you create (audio, lyrics, cover art), vocal takes you keep, stems and masters, your email and name when you sign in, and payment records from Stripe (we never see your card number).</p>
            <p><strong className="text-white">Who processes your audio:</strong> music generation, stem separation and voice tools run on trusted partner services (music generation, audio separation, voice and image models). Your audio is sent to them only to perform the action you asked for.</p>
            <p><strong className="text-white">Deleting:</strong> deleted projects sit in the Deleted tab for 30 days, then they're removed for good. You can delete forever at any time. To close your account entirely, email us.</p>
          </Section>

          <Section id="support" icon={LifeBuoy} title="Support">
            <p>Stuck, or something didn't sound right? We'd love to help.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Fact icon={Mail} title="Email">
                <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="about-support-email" className="font-semibold text-blue-200 underline-offset-2 hover:underline">{SUPPORT_EMAIL}</a> — we aim to reply within two working days.
              </Fact>
              <Fact icon={ScrollText} title="In-app help">The chat bubble in the bottom-right corner answers common questions instantly and can pass a report to us.</Fact>
            </div>
            <p className="text-[14px] text-white/70">When you write, mention the song title and roughly when it happened — it helps us find it quickly.</p>
          </Section>
        </div>
      </div>
    </CreatorShell>
  );
}
